import { useCallback, useEffect, useState } from 'react';

// UI density mode. PURELY PRESENTATIONAL — nothing here changes behaviour,
// permissions, or what the app does. 'advanced' renders every control the app
// has ever had; 'novice' hides the secondary ones (priority, star, snooze,
// AI-flag review, bulk select, the parked "waiting" queues, technical customer
// fields…) so the screen isn't over-packed. Flipping back to 'advanced'
// restores all of them.
//
// LABELS ARE NOT SECONDARY — they used to be listed above and no longer are.
// The AI tags every conversation in production, but the chips, the picker and
// the Labels filter all sat behind this toggle, which defaults to 'novice'. The
// result: tagging was reported as a MISSING feature while it was quietly
// running the whole time. Anything label-shaped renders in both modes; only the
// bulk-select tooling around it stays advanced-only.
//
// THIRD MODE — 'new'. Purely a LAYOUT swap: the inbox chrome (scope tabs + the
// horizontal view-tab bar) moves into a collapsible left rail. It is additive.
// 'novice' and 'advanced' render exactly what they rendered before, and every
// existing consumer reads `advanced`, which stays false in 'new' — so the new
// mode inherits the simple density and nothing downstream had to change.
export type UiMode = 'novice' | 'advanced' | 'new';

/** Inbox chrome shape. 'classic' === everything shipped before the rail. */
export type UiLayout = 'classic' | 'rail';

const KEY = 'chatolam:uiMode';
const EVENT = 'chatolam:uiModeChanged';

// novice → advanced → new → novice
const CYCLE: Record<UiMode, UiMode> = { novice: 'advanced', advanced: 'new', new: 'novice' };

export function readUiMode(): UiMode {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'advanced' || v === 'new' ? v : 'novice';
  } catch {
    return 'novice';
  }
}

export function writeUiMode(mode: UiMode): void {
  try {
    localStorage.setItem(KEY, mode);
  } catch {
    /* ignore — the toggle still works for this session via the event */
  }
  window.dispatchEvent(new CustomEvent<UiMode>(EVENT, { detail: mode }));
}

/**
 * Live UI-density mode, shared by every mounted component (custom event) and
 * across tabs (storage event). `advanced === true` means "show everything".
 */
export function useUiMode(): { mode: UiMode; advanced: boolean; layout: UiLayout; toggle: () => void } {
  const [mode, setMode] = useState<UiMode>(readUiMode);

  useEffect(() => {
    const onChange = (e: Event) => {
      const detail = (e as CustomEvent<UiMode>).detail;
      const known = detail === 'advanced' || detail === 'novice' || detail === 'new';
      setMode(known ? detail : readUiMode());
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === null || e.key === KEY) setMode(readUiMode());
    };
    window.addEventListener(EVENT, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const toggle = useCallback(() => {
    writeUiMode(CYCLE[readUiMode()]);
  }, []);

  // `advanced` deliberately stays a strict 'advanced' check: the rail is a
  // layout, not a density, so every existing consumer behaves in 'new' exactly
  // as it does in the (default) simple view.
  return {
    mode,
    advanced: mode === 'advanced',
    layout: mode === 'new' ? 'rail' : 'classic',
    toggle,
  };
}
